import React from "react";
import { motion } from "framer-motion";
import { BRAND, FEATURES } from "@/data/adPackage";
import { FeatureIcon, OrderTrackingMockup, PhoneFrame } from "@/components/UiMockups";
import { Search, ShoppingCart, Navigation, PartyPopper } from "lucide-react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const pop = {
  hidden: { opacity: 0, scale: 0.7 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function Eyebrow({ children }) {
  return (
    <motion.div variants={item} className="inline-flex items-center gap-2 mb-3 sm:mb-4">
      <span className="w-6 sm:w-8 h-px bg-orange-400" />
      <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-orange-400">
        {children}
      </span>
    </motion.div>
  );
}

// NOTE: Shell MUST stay at module scope. Defining it inside SceneOverlay gives it a
// new identity on every parent re-render (the player calls setState ~60fps), which
// remounts its subtree each frame and resets the entrance animation to opacity:0.
function Shell({ children, align = "left" }) {
  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className={`relative z-20 h-full w-full flex flex-col justify-center px-6 sm:px-12 lg:px-20 ${
        align === "center" ? "items-center text-center" : "items-start"
      }`}
    >
      <div className={align === "center" ? "max-w-3xl" : "max-w-2xl"}>{children}</div>
    </motion.div>
  );
}

function SceneOverlay({ scene }) {
  const o = scene.overlay || {};

  switch (scene.variant) {
    case "headline":
      return (
        <Shell>
          <Eyebrow>{o.eyebrow}</Eyebrow>
          <motion.h2 variants={item} className="font-display font-black text-white leading-[0.95] text-3xl sm:text-5xl lg:text-6xl tracking-tight">
            {o.big}
          </motion.h2>
          <motion.p variants={item} className="mt-4 text-neutral-300 text-sm sm:text-lg max-w-md">
            {o.sub}
          </motion.p>
        </Shell>
      );

    case "brand":
      return (
        <Shell align="center">
          <motion.img variants={pop} src={BRAND.logo} alt="BiteGoFood" className="w-24 sm:w-32 lg:w-40 mx-auto rounded-3xl shadow-2xl floaty" />
          <motion.h2 variants={item} className="mt-5 font-display font-black text-white text-4xl sm:text-6xl lg:text-7xl tracking-tight">
            {o.big}
          </motion.h2>
          <motion.p variants={item} className="mt-2 text-neutral-200 text-base sm:text-2xl">
            {o.sub}
          </motion.p>
          <motion.span variants={pop} className="mt-5 inline-block px-5 py-2 rounded-full brand-gradient text-white font-bold text-sm sm:text-lg tracking-wide">
            {o.pill}
          </motion.span>
        </Shell>
      );

    case "commission":
      return (
        <motion.div variants={container} initial="hidden" animate="show" className="relative z-20 h-full w-full grid grid-cols-1 lg:grid-cols-2 items-center gap-6 px-6 sm:px-12 lg:px-16">
          <div>
            <Eyebrow>{o.eyebrow}</Eyebrow>
            <motion.div variants={pop} className="font-display font-black leading-none">
              <span className="brand-text text-6xl sm:text-8xl lg:text-9xl">ONLY 18%</span>
            </motion.div>
            <motion.div variants={item} className="font-display font-black text-white text-2xl sm:text-4xl lg:text-5xl tracking-tight -mt-1">
              COMMISSION
            </motion.div>
            <motion.p variants={item} className="mt-3 text-neutral-300 text-sm sm:text-xl">
              {o.sub}
            </motion.p>
          </div>
          <motion.div variants={pop} className="glass rounded-2xl p-5 sm:p-7 max-w-sm w-full mx-auto">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-orange-400 font-semibold">{o.pricingTitle}</div>
            <div className="mt-3 font-display font-black text-white text-xl sm:text-3xl leading-tight">
              {o.pricingLine}
            </div>
            <div className="mt-4 space-y-2 text-sm sm:text-base">
              <div className="flex justify-between text-neutral-300"><span>Food Cost</span><span className="text-white font-semibold">shown clearly</span></div>
              <div className="flex justify-between text-neutral-300"><span>Delivery Fee</span><span className="text-white font-semibold">applicable</span></div>
              <div className="h-px bg-white/10" />
              <div className="flex justify-between"><span className="text-white font-semibold">What you pay</span><span className="brand-text font-display font-black">Transparent</span></div>
            </div>
          </motion.div>
        </motion.div>
      );

    case "features":
      return (
        <Shell>
          <Eyebrow>{o.eyebrow}</Eyebrow>
          <motion.h2 variants={item} className="font-display font-black text-white text-2xl sm:text-4xl tracking-tight leading-tight max-w-xl">
            {o.big}
          </motion.h2>
          <div className="mt-4 sm:mt-5 grid grid-cols-2 gap-2 max-w-2xl">
            {FEATURES.map((f) => (
              <motion.div key={f.label} variants={item} className="flex items-center gap-2 glass rounded-lg px-2.5 py-1.5">
                <FeatureIcon name={f.icon} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 shrink-0" />
                <span className="text-[10px] sm:text-xs text-white font-medium leading-tight">{f.label}</span>
              </motion.div>
            ))}
          </div>
        </Shell>
      );

    case "flow": {
      const flowIcons = [Search, ShoppingCart, Navigation, PartyPopper];
      return (
        <motion.div variants={container} initial="hidden" animate="show" className="relative z-20 h-full w-full grid grid-cols-1 lg:grid-cols-2 items-center gap-8 px-6 sm:px-12 lg:px-16">
          <div>
            <Eyebrow>{o.eyebrow}</Eyebrow>
            <motion.h2 variants={item} className="font-display font-black text-white text-4xl sm:text-6xl tracking-tight">
              {o.big}
            </motion.h2>
            <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
              {o.steps.map((s, i) => {
                const Ico = flowIcons[i];
                return (
                  <motion.div key={s} variants={pop} className="flex items-center gap-2 glass rounded-full pl-2 pr-4 py-1.5">
                    <span className="w-7 h-7 rounded-full brand-gradient flex items-center justify-center text-white">
                      <Ico className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-sm sm:text-base font-semibold text-white">{s}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
          <motion.div variants={pop} className="hidden lg:block">
            <PhoneFrame>
              <OrderTrackingMockup />
            </PhoneFrame>
          </motion.div>
        </motion.div>
      );
    }

    case "growth":
      return (
        <Shell>
          <Eyebrow>{o.eyebrow}</Eyebrow>
          <div className="space-y-1 sm:space-y-2">
            {o.lines.map((l, i) => (
              <motion.div
                key={l}
                variants={item}
                className={`font-display font-black tracking-tight leading-[0.95] ${
                  i === 0 ? "brand-text text-5xl sm:text-7xl lg:text-8xl" : "text-white text-3xl sm:text-5xl lg:text-6xl"
                }`}
              >
                {l}
              </motion.div>
            ))}
          </div>
        </Shell>
      );

    case "cta":
      return (
        <Shell align="center">
          <motion.img variants={pop} src={BRAND.logo} alt="BiteGoFood" className="w-14 sm:w-20 mx-auto rounded-3xl shadow-2xl" />
          <motion.div variants={item} className="mt-2 font-display font-black text-white text-xl sm:text-3xl tracking-tight">{BRAND.name}</motion.div>
          <motion.div variants={item} className="text-[10px] sm:text-sm text-neutral-300">{BRAND.tagline}</motion.div>
          <motion.div variants={item} className="mt-2 inline-block px-4 py-1 rounded-full brand-gradient text-white font-bold text-[10px] sm:text-sm tracking-wide">
            {o.commission}
          </motion.div>
          <motion.h2 variants={item} className="mt-2 font-display font-black text-white text-xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.02]">
            {o.big} <span className="brand-text">{o.big2}</span>
          </motion.h2>
          <motion.a
            variants={pop}
            href={BRAND.websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white text-black font-display font-bold text-sm sm:text-lg"
          >
            {o.cta}
          </motion.a>
          <motion.div variants={item} className="mt-2 text-[10px] sm:text-xs text-neutral-300">
            Restaurant Partnerships: <span className="text-orange-400 font-medium">{BRAND.partnerEmail}</span>
          </motion.div>
          <motion.div variants={item} className="mt-1 text-[9px] sm:text-[11px] text-neutral-500 tracking-wide">
            {BRAND.poweredBy}
          </motion.div>
        </Shell>
      );

    default:
      return null;
  }
}

export default React.memo(SceneOverlay);

